import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isKoreanHoliday, getHolidayName, isWeekend } from '@bhops/shared';
import type { ManufacturingCalendarCategory, ManufacturingCalendarEventDto } from '@bhops/shared';
import { teamDashboardApi } from '@/api/team-dashboard';
import { robotsApi } from '@/api/robots';
import { serviceOrdersApi } from '@/api/service-orders';
import { kstMonthCursor, kstTodayYmd, kstYmdHyphen } from '@/lib/format-date';
import { Icon, type IconName } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';
import { robotUsableForUser, robotUseDeniedMessage } from '@/pages/robots/robot-entity';
import { RobotEntityTag } from '@/pages/robots/RobotEntityTag';

// S3 Phase 3.8 (spec §3.9) — 제조 대시보드 전용 캘린더.
//
// IntegratedCalendar(Phase 3.7)의 월-그리드 + 토글 바 + localStorage 패턴을
// 재사용하되, 이벤트 소스는 제조 전용 단일 엔드포인트
// `/dashboard/manufacturing-calendar` (4 카테고리):
//   📐 제조 계획 (production) — ProductionPlan.estimatedCompletionAt
//   🚚 출고      (ship)       — 계약 예약 로봇의 InfraOrder.installScheduledAt
//   🔁 로봇 교체 (replacement)— ServiceOrder(ROBOT_REPLACE) scheduledFor
//   📦 회수      (return)     — Customer.returnScheduledAt
// 유지보수 / 그릴러 납품은 필드 영역이라 제외 (IntegratedCalendar 소관).
//
// 교체 이벤트 카드에는 [출고 대기 로봇 할당 ▾] 컨트롤이 붙어
// READY_TO_SHIP 로봇을 그 교체 SO 에 할당한다(POST /service-orders/:id/
// replacement-robot) → 캘린더 + 로봇 쿼리 invalidate.
//
// spec §3.9 표시: [✓제조 계획] [✓출고] [✓교체] [✓회수] — 전부 기본 ON.

type Category = ManufacturingCalendarCategory;

// round6 §1-4(b) — 'ship-done'(설치 완료 이력) 추가: 완료 처리 순간 출고
// 이벤트가 사라지던 문제 해소.
const CATEGORY_ORDER: Category[] = ['production', 'ship', 'ship-done', 'replacement', 'return'];

const CATEGORY_META: Record<
  Category,
  { iconName: IconName; label: string; color: string; bg: string }
> = {
  production: {
    iconName: 'factory',
    label: '제조 계획',
    color: 'var(--brand)',
    bg: 'var(--brand-subtle)',
  },
  ship: {
    iconName: 'truck',
    label: '출고',
    color: 'var(--blue-800)',
    bg: 'var(--blue-50)',
  },
  'ship-done': {
    iconName: 'check-circle',
    label: '설치 완료',
    color: 'var(--fg-success)',
    bg: 'var(--bg-success-subtle)',
  },
  // §9(아이콘 정본화) — refresh 는 '데이터 새로고침' 전용으로 비웠다.
  // 로봇 교체는 swap(양방향 화살표).
  replacement: {
    iconName: 'swap',
    label: '교체',
    color: 'var(--dday-expired)',
    bg: 'var(--bg-danger-subtle)',
  },
  return: {
    iconName: 'package',
    label: '회수',
    color: 'var(--dday-warning)',
    bg: 'var(--bg-warning-subtle)',
  },
};

// spec §3.9 — 4 카테고리 전부 기본 ON.
const CATEGORY_STORAGE_KEY = 'kuun:manufacturing-calendar:categories';

const DEFAULT_ACTIVE: Record<Category, boolean> = {
  production: true,
  ship: true,
  'ship-done': true,
  replacement: true,
  return: true,
};

function loadActiveCategories(): Record<Category, boolean> {
  if (typeof window === 'undefined') return { ...DEFAULT_ACTIVE };
  try {
    const raw = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_ACTIVE };
    const parsed = JSON.parse(raw) as Partial<Record<Category, unknown>>;
    return {
      production:
        typeof parsed.production === 'boolean' ? parsed.production : DEFAULT_ACTIVE.production,
      ship: typeof parsed.ship === 'boolean' ? parsed.ship : DEFAULT_ACTIVE.ship,
      'ship-done':
        typeof parsed['ship-done'] === 'boolean'
          ? parsed['ship-done']
          : DEFAULT_ACTIVE['ship-done'],
      replacement:
        typeof parsed.replacement === 'boolean' ? parsed.replacement : DEFAULT_ACTIVE.replacement,
      return: typeof parsed.return === 'boolean' ? parsed.return : DEFAULT_ACTIVE.return,
    };
  } catch {
    return { ...DEFAULT_ACTIVE };
  }
}

function saveActiveCategories(active: Record<Category, boolean>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(active));
  } catch {
    // localStorage quota / private mode — silently ignore.
  }
}

interface Props {
  /** Initial month to display (YYYY-MM-DD; day ignored). Defaults to today. */
  initialMonth?: string;
}

export function ManufacturingCalendar({ initialMonth }: Props) {
  // 한국시간 통일 5차(26-08-14) — 격자 커서를 KST 로. 종전 `new Date().getUTCMonth()`
  // 는 한국 1월 1일 오전 0~9시에 **작년 12월**을 그렸다(그때 UTC 는 아직 12/31).
  const [cursor, setCursor] = useState(() => kstMonthCursor(initialMonth));

  const [activeCategories, setActiveCategories] =
    useState<Record<Category, boolean>>(loadActiveCategories);

  useEffect(() => {
    saveActiveCategories(activeCategories);
  }, [activeCategories]);

  function toggleCategory(cat: Category): void {
    setActiveCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  }

  const range = useMemo(() => monthGridRange(cursor.year, cursor.month), [cursor]);

  const calQ = useQuery({
    queryKey: ['dashboard', 'manufacturing-calendar', range.start, range.end],
    queryFn: () => teamDashboardApi.manufacturingCalendar(range.start, range.end),
    staleTime: 60_000,
  });

  const allEvents = useMemo(() => calQ.data?.events ?? [], [calQ.data]);

  const events = useMemo(
    () => allEvents.filter((e) => activeCategories[e.category]),
    [allEvents, activeCategories],
  );

  const eventsByDate = useMemo(() => {
    const m = new Map<string, ManufacturingCalendarEventDto[]>();
    for (const e of events) {
      const arr = m.get(e.date);
      if (arr) arr.push(e);
      else m.set(e.date, [e]);
    }
    return m;
  }, [events]);

  const weeks = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);

  // 교체 카드 패널을 펼친 이벤트 id (단일 — 한 번에 하나만 펼침).
  const [openReplacementId, setOpenReplacementId] = useState<string | null>(null);

  function shift(delta: number): void {
    setCursor((c) => {
      const m = c.month + delta;
      if (m < 0) return { year: c.year - 1, month: 11 };
      if (m > 11) return { year: c.year + 1, month: 0 };
      return { year: c.year, month: m };
    });
  }

  function jumpToToday(): void {
    setCursor(kstMonthCursor());
  }

  // '오늘' 테두리가 붙는 칸. 서버가 이벤트를 KST 키로 보내므로(dashboard.service
  // 의 kstDayKey) 이 값도 KST 여야 같은 칸을 가리킨다. 종전 UTC 읽기는 한국
  // 오전 0~9시에 **어제 칸**에 테두리를 그렸다 — 그날 일정은 옆 칸에 있는데.
  const today = kstTodayYmd();

  // 교체 이벤트만 별도로 모아 캘린더 아래 "교체 일정 카드" 리스트로 렌더
  // (할당 컨트롤이 그리드 셀에는 너무 좁아서 — spec §3.9 카드 UX).
  const replacementEvents = useMemo(
    () => events.filter((e) => e.category === 'replacement' && e.replacement),
    [events],
  );

  return (
    <section style={card} aria-label="제조 캘린더">
      <header style={headerRow}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 className="kuun-h3" style={{ margin: 0 }}>
            제조 캘린더
          </h3>
          <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
            {calQ.isFetching ? '불러오는 중…' : `${events.length}건`}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button type="button" onClick={() => shift(-1)} style={navBtn} aria-label="이전 달">
            ‹
          </button>
          <strong style={{ minWidth: 110, textAlign: 'center', fontSize: 14 }}>
            {cursor.year}년 {String(cursor.month + 1).padStart(2, '0')}월
          </strong>
          <button type="button" onClick={() => shift(1)} style={navBtn} aria-label="다음 달">
            ›
          </button>
          <button type="button" onClick={jumpToToday} style={todayBtn}>
            오늘
          </button>
        </div>
      </header>

      {/* spec §3.9 — 표시: [✓제조 계획] [✓출고] [✓교체] [✓회수] (전부 기본 ON). */}
      <div style={toggleBar} role="group" aria-label="제조 캘린더 카테고리 표시 토글">
        <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 600 }}>표시:</span>
        {CATEGORY_ORDER.map((k) => {
          const on = activeCategories[k];
          return (
            <label key={k} style={toggleChip(k, on)}>
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggleCategory(k)}
                style={{ margin: 0, cursor: 'pointer' }}
              />
              <Icon name={CATEGORY_META[k].iconName} size={14} aria-hidden />{' '}
              {CATEGORY_META[k].label}
            </label>
          );
        })}
      </div>

      <div style={gridHeader}>
        {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
          <div
            key={d}
            style={{
              ...gridHeaderCell,
              color: i === 0 ? 'var(--fg-danger)' : i === 6 ? 'var(--blue-800)' : undefined,
            }}
          >
            {d}
          </div>
        ))}
      </div>

      <div style={grid}>
        {weeks.flat().map((cell) => {
          const isToday = cell.dateStr === today;
          const inMonth = cell.month === cursor.month;
          const dayEvents = eventsByDate.get(cell.dateStr) ?? [];
          const holiday = isKoreanHoliday(cell.dateStr);
          const weekend = isWeekend(cell.dateStr);
          const maxChips = 3;

          return (
            <div key={cell.dateStr} style={dayCell(inMonth, isToday)}>
              <div style={dayHeader}>
                <span style={dateWithTag}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: isToday ? 700 : 500,
                      color: holiday
                        ? 'var(--fg-danger)'
                        : weekend && cell.day === 0
                          ? 'var(--fg-danger)'
                          : weekend && cell.day === 6
                            ? 'var(--blue-800)'
                            : inMonth
                              ? 'var(--fg-default)'
                              : 'var(--fg-muted)',
                    }}
                  >
                    {cell.dateStr.slice(8, 10)}
                  </span>
                  {isToday && (
                    <span style={todayTag} data-testid={`cal-today-${cell.dateStr}`}>
                      오늘
                    </span>
                  )}
                </span>
                {holiday && (
                  <span
                    title={getHolidayName(cell.dateStr)}
                    style={{ fontSize: 9, color: 'var(--fg-danger)' }}
                  >
                    {getHolidayName(cell.dateStr)}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {dayEvents.slice(0, maxChips).map((ev) => {
                  const meta = CATEGORY_META[ev.category];
                  return (
                    <span
                      key={ev.id}
                      style={{
                        ...eventChip,
                        background: meta.bg,
                        color: meta.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                      }}
                      title={`${meta.label} — ${ev.label}`}
                    >
                      <Icon name={meta.iconName} size={11} aria-hidden /> {ev.label}
                    </span>
                  );
                })}
                {dayEvents.length > maxChips && (
                  <span style={{ fontSize: 9, color: 'var(--fg-muted)' }}>
                    +{dayEvents.length - maxChips} 건 더
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* spec §3.9 — 교체 일정 카드 + [출고 대기 로봇 할당 ▾]. 교체 토글이
          켜져 있고 교체 이벤트가 있을 때만 렌더. */}
      {activeCategories.replacement && replacementEvents.length > 0 && (
        <div style={replacementSection} aria-label="로봇 교체 일정">
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--dday-expired)',
              marginBottom: 6,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <Icon name="swap" size={14} aria-hidden /> 로봇 교체 일정 ({replacementEvents.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {replacementEvents.map((ev) => (
              <ReplacementCard
                key={ev.id}
                event={ev}
                open={openReplacementId === ev.id}
                onToggleOpen={() => setOpenReplacementId((cur) => (cur === ev.id ? null : ev.id))}
                onAssigned={() => setOpenReplacementId(null)}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================
// 교체 카드 — 대상 표시 + [출고 대기 로봇 할당 ▾] (READY_TO_SHIP 목록)
// ============================================================

function ReplacementCard({
  event,
  open,
  onToggleOpen,
  onAssigned,
}: {
  event: ManufacturingCalendarEventDto;
  open: boolean;
  onToggleOpen: () => void;
  onAssigned: () => void;
}) {
  const meta = event.replacement!;
  const queryClient = useQueryClient();
  const assigned = meta.replacementRobotId != null;

  // READY_TO_SHIP 로봇 목록 — 패널이 열리고 아직 미할당일 때만 fetch.
  const robotsQ = useQuery({
    queryKey: ['robots', 'ready-to-ship', 'replacement-picker'],
    enabled: open && !assigned,
    queryFn: () => robotsApi.list({ status: 'READY_TO_SHIP' as never, pageSize: 100 }),
    staleTime: 30_000,
  });
  // 오너 결정 2026-10-20 — 직원도 허니컴로보틱스 로봇을 보고 고친다. 그래서 위 목록에
  // 허니컴로보틱스 로봇이 섞여 온다. 10-20 에는 교체 로봇 할당(service-orders
  // assignReplacementRobot)이 관리자가 아니면 자기 법인 로봇만 받아서 여기서도 뺐다.
  // 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치. 교체 할당도 '이 교체
  // 오더(고객사 쪽) 법인에 쓸 수 있는 로봇'을 받는다(서버 canUseRobotAt). 이 응답에는 고객사의 법인이
  // 실리지 않으므로 robotUsableForUser 로 본다 — 관리자가 아니면 교체 오더는 언제나 자기 법인
  // 것이라 그 법인에 쓸 수 있는 로봇만(= 비욘드허니컴 직원에게는 두 법인 로봇 모두) 남기고,
  // 관리자는 거르지 않고 서버에 맡긴다. 다른 법인 로봇에는 법인 이름을 붙인다(소유 법인은 그대로).
  const authUser = useAuthStore((st) => st.user);
  const readyRobots = robotsQ.data?.items ?? [];
  const pickable = readyRobots.filter((r) => robotUsableForUser(authUser, r));
  // 출고 대기 로봇은 있는데 하나도 쓸 수 없으면 '없다'고만 말하면 거짓이다 — 서버와 같은 말로 이유를.
  const unusable = pickable.length === 0 ? readyRobots[0] : undefined;

  const assignM = useMutation({
    mutationFn: (robotId: string) =>
      serviceOrdersApi.assignReplacementRobot(meta.serviceOrderId, { robotId }),
    onSuccess: () => {
      // 캘린더 + 로봇 쿼리 invalidate → 카드가 '할당됨' 상태로 갱신.
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'manufacturing-calendar'] });
      void queryClient.invalidateQueries({ queryKey: ['robots'] });
      onAssigned();
    },
  });

  return (
    <div style={replacementCard} data-testid="replacement-card">
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <div style={{ fontSize: 12 }}>
          <strong>{event.date}</strong> 로봇 교체: {meta.customerName}{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            {meta.targetRobotSerial ? `#${meta.targetRobotSerial}` : '대상 미상'}
          </span>
          <span style={{ marginLeft: 6, color: 'var(--fg-muted)', fontSize: 11 }}>
            필드팀 요청 {meta.serviceOrderHumanId}
          </span>
        </div>
      </div>

      {assigned ? (
        // 이미 할당된 경우 — 할당 로봇 표시 (드롭다운 숨김).
        <div style={assignedBadge} data-testid="assigned-robot">
          <Icon name="check" size={14} aria-hidden /> 할당한 출고 대기 로봇:{' '}
          <strong style={{ fontFamily: 'var(--font-mono)' }}>
            {meta.replacementRobotSerial
              ? `#${meta.replacementRobotSerial}`
              : meta.replacementRobotId}
          </strong>
          . 이 로봇은 설치 대기로 바뀌었습니다.
        </div>
      ) : (
        <div style={{ marginTop: 6 }}>
          <button type="button" onClick={onToggleOpen} style={assignToggleBtn} aria-expanded={open}>
            출고 대기 로봇 할당{' '}
            {open ? (
              <Icon name="chev-up" size={14} aria-hidden />
            ) : (
              <Icon name="chev-down" size={14} aria-hidden />
            )}
          </button>

          {open && (
            <div style={pickerPanel}>
              {robotsQ.isLoading && (
                <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>로봇 목록 불러오는 중…</div>
              )}
              {robotsQ.error && (
                <div style={{ fontSize: 11, color: 'var(--fg-danger)' }}>
                  로봇 목록을 불러오지 못했습니다.
                </div>
              )}
              {robotsQ.data && pickable.length === 0 && (
                <div
                  style={{ fontSize: 11, color: 'var(--fg-muted)' }}
                  data-testid="replacement-picker-empty"
                >
                  {unusable && authUser
                    ? `이 교체에 쓸 수 있는 출고 대기 로봇이 없습니다. ${robotUseDeniedMessage(unusable.ownerEntity, authUser.ownerEntity)}`
                    : '출고 대기 로봇이 없습니다.'}
                </div>
              )}
              {robotsQ.data && pickable.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {pickable.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      disabled={assignM.isPending}
                      onClick={() => assignM.mutate(r.id)}
                      style={robotPickRow}
                    >
                      <span style={{ fontFamily: 'var(--font-mono)' }}>
                        {r.serialShort ? `#${r.serialShort}` : (r.serialLong ?? r.id)}
                      </span>
                      <span style={{ color: 'var(--fg-muted)' }}>
                        {r.model.displayName} 출고 대기
                      </span>
                      {/* 고객사 법인을 이 응답이 모르므로 내 계정의 법인과 견준다. */}
                      <RobotEntityTag
                        robotEntity={r.ownerEntity}
                        referenceEntity={authUser?.ownerEntity}
                      />
                    </button>
                  ))}
                </div>
              )}
              {assignM.error && (
                <div style={{ fontSize: 11, color: 'var(--fg-danger)', marginTop: 4 }}>
                  할당하지 못했습니다. 다시 시도해 주세요.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 격자 만들기 — 셀 라벨은 **정본 한 벌**(kstYmdHyphen)로 찍는다
// ============================================================
//
// 한국시간 통일 5차(26-08-14) — 여기 있던 지역 `formatYmd`(UTC 성분 읽기)를 지웠다.
// 격자 날짜는 `Date.UTC(y, m, d)` 로 만든 **UTC 자정**이라 KST 로 읽어도 같은
// 날짜다(자정 + 9h = 그날 09:00). 즉 출력은 종전과 한 글자도 다르지 않으면서
// 이 파일에서 +9h/UTC 규칙을 아는 코드가 사라진다 — 다음 사람이 '오늘'만
// 고치고 격자를 안 고쳐 둘이 갈리는 사고를 구조적으로 막는다.

/** 격자용 UTC 자정 Date → "YYYY-MM-DD". 항상 유효하므로 폴백은 방어용. */
function gridYmd(d: Date): string {
  return kstYmdHyphen(d) ?? '';
}

interface GridCell {
  dateStr: string;
  month: number;
  day: number;
}

function buildMonthGrid(year: number, month: number): GridCell[][] {
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());

  const weeks: GridCell[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: GridCell[] = [];
    for (let d = 0; d < 7; d++) {
      week.push({
        dateStr: gridYmd(cursor),
        month: cursor.getUTCMonth(),
        day: cursor.getUTCDay(),
      });
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
}

function monthGridRange(year: number, month: number): { start: string; end: string } {
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first);
  start.setUTCDate(start.getUTCDate() - start.getUTCDay());
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 41);
  return { start: gridYmd(start), end: gridYmd(end) };
}

// ============================================================
// Styles (shared shape with IntegratedCalendar)
// ============================================================

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--bg-surface)',
  padding: 16,
};

const headerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
  flexWrap: 'wrap',
  gap: 8,
};

const navBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  width: 28,
  height: 28,
  cursor: 'pointer',
  fontSize: 14,
  color: 'var(--fg-default)',
};

const todayBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  height: 28,
  padding: '0 10px',
  marginLeft: 8,
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  color: 'var(--fg-default)',
};

const toggleBar: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 6,
  marginBottom: 8,
};

function toggleChip(c: Category, on: boolean): CSSProperties {
  const m = CATEGORY_META[c];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid',
    borderColor: on ? 'transparent' : 'var(--border-default)',
    background: on ? m.bg : 'transparent',
    color: on ? m.color : 'var(--fg-muted)',
    fontWeight: 500,
    cursor: 'pointer',
    userSelect: 'none',
  };
}

const gridHeader: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  borderBottom: '1px solid var(--border-subtle)',
};

const gridHeaderCell: CSSProperties = {
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 600,
  padding: '6px 0',
  color: 'var(--fg-muted)',
};

const grid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gridAutoRows: 'minmax(80px, auto)',
};

function dayCell(inMonth: boolean, isToday: boolean): CSSProperties {
  return {
    border: '1px solid var(--border-default)',
    borderTop: 'none',
    borderLeft: 'none',
    padding: 4,
    background: isToday ? 'var(--blue-50)' : inMonth ? 'var(--bg-surface)' : 'var(--bg-sunken)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minHeight: 80,
    overflow: 'hidden',
  };
}

/**
 * 오늘 칸의 '오늘' 꼬리표 — 날짜 글자와 같은 크기, 진한 브랜드색 바탕에 흰 글씨(2026-10-23 오너 "날짜 옆에 날짜 텍스트
 * 크기만한 크기로 '오늘' … 잘보이는 색으로 강조"). 칸 바탕(--blue-50)보다 훨씬 진해 한눈에 띈다.
 */
const todayTag: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '16px',
  padding: '0 6px',
  borderRadius: 999,
  background: 'var(--brand)',
  color: 'var(--brand-on)',
  whiteSpace: 'nowrap',
};
const dateWithTag: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4 };

const dayHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
};

const eventChip: CSSProperties = {
  display: 'inline-block',
  width: '100%',
  fontSize: 10,
  padding: '2px 6px',
  borderRadius: 'var(--radius-sm)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const replacementSection: CSSProperties = {
  marginTop: 12,
  paddingTop: 12,
  borderTop: '1px solid var(--border-subtle)',
};

const replacementCard: CSSProperties = {
  border: '1px solid var(--border-danger-saturated)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-danger-subtle)',
  padding: 10,
};

const assignedBadge: CSSProperties = {
  marginTop: 6,
  fontSize: 11,
  color: 'var(--fg-on-success-tint)',
  background: 'var(--bg-success-subtle)',
  borderRadius: 'var(--radius-sm)',
  padding: '4px 8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flexWrap: 'wrap',
};

const assignToggleBtn: CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-danger-saturated)',
  borderRadius: 'var(--radius-sm)',
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--dday-expired)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const pickerPanel: CSSProperties = {
  marginTop: 6,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  padding: 8,
  maxHeight: 220,
  overflowY: 'auto',
};

const robotPickRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  width: '100%',
  textAlign: 'left',
  background: 'transparent',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  padding: '4px 8px',
  fontSize: 11,
  cursor: 'pointer',
};
